/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */
import Promise from 'bluebird';
import _ from 'underscore';

import { levelAtConstant } from '@bingads-webui-campaign/scope-constants';
import { parseSync } from '@bingads-webui/safe-json';
import { Selector } from '@bingads-webui/selector';

import { KeywordDataSource, formatKeywordName } from './keyword-data-source';
import { AdGroupDataSource, formatAdGroupName } from './adgroup-data-source';
import { CampaignDataSource } from './campaign-data-source';
import { MAX_SELECTION_COUNT } from './consts';

/* eslint-disable no-param-reassign */

const stubDataSource = {
  findAll: () => Promise.resolve([]),
};

export const selectorStyleOptions = {
  list: ['table--fixed-layout', 'table--no-border'],
  nameColumn: ['table__cell--menu-item-style'],
};

export const selectorKeyMapFunc = i18n => ({
  Selector_Filter_Selected: (unused, key, params) => i18n.getString('CompetitionTab_Selector_Filter_Selected', params),
  Selector_Filter_All: () => i18n.getString('CompetitionTab_Selector_Filter_All'),
});

export function handleFilterChange({
  viewFilter,
  searchFilter,
  stats,
  dataSource,
  selector,
  missingDataHandler,
}) {
  let ids;
  if (viewFilter === 'selected') {
    ids = _.pluck(stats.checked, 'id');
  }
  return dataSource.get(viewFilter, searchFilter, ids, stats).then((data) => {
    let predicate = _.identity;

    const { checkMap } = selector;
    const missingIds = _.difference(_.keys(checkMap), _.pluck(data, 'Id'));
    const patchedData = missingDataHandler({
      data,
      missingIds,
    });

    if (viewFilter === Selector.ViewFilter.selected) {
      const selected = stats.checked;
      predicate = item => item && _.find(selected, ({ id }) => id === item.Id);
    }

    selector.setData(_.filter(patchedData, predicate));
  });
}

export function handleFilterChangeForCampaign({
  viewFilter,
  searchFilter,
  stats,
  dataSource,
  selector,
}) {
  handleFilterChange({
    viewFilter,
    searchFilter,
    stats,
    dataSource,
    selector,
    missingDataHandler: ({ data, missingIds }) => {
      let missingData = [];
      _.each(missingIds, (missingId) => {
        missingData.push({
          Id: missingId,
          Name: parseSync(missingId).Name,
        });
      });

      if (searchFilter) {
        missingData = _.filter(
          missingData,
          entity => searchFilter.indexOf(entity && entity.Name) >= 0
        );
      }

      return missingData.concat(data);
    },
  });
}

export function handleFilterChangeForAdGroup({
  viewFilter,
  searchFilter,
  stats,
  dataSource,
  selector,
}) {
  handleFilterChange({
    viewFilter,
    searchFilter,
    stats,
    dataSource,
    selector,
    missingDataHandler: ({ data, missingIds }) => {
      let missingData = [];
      _.each(missingIds, (missingId) => {
        missingData.push({
          Id: missingId,
          Name: formatAdGroupName({
            adgroupName: parseSync(missingId).Name,
            campaignName: parseSync(missingId).CampaignName,
          }),
        });
      });

      if (searchFilter) {
        missingData = _.filter(
          missingData,
          entity => searchFilter.indexOf(entity && entity.Name) >= 0
        );
      }

      return missingData.concat(data);
    },
  });
}

export function handleFilterChangeForKeyword({
  viewFilter,
  searchFilter,
  stats,
  dataSource,
  selector,
}) {
  handleFilterChange({
    viewFilter,
    searchFilter,
    stats,
    dataSource,
    selector,
    missingDataHandler: ({ data, missingIds }) => {
      let missingData = [];
      _.each(missingIds, (missingId) => {
        const parsed = parseSync(missingId);

        missingData.push({
          Id: missingId,
          Name: formatKeywordName({
            keywordName: parsed.Name,
            adgroupName: parsed.AdGroupName,
            campaignName: parsed.CampaignName,
          }),
        });
      });

      if (searchFilter) {
        missingData = _.filter(
          missingData,
          entity => searchFilter.indexOf(entity && entity.Name) >= 0
        );
      }

      return missingData.concat(data);
    },
  });
}

function getSelectorOptions(i18n, selectorOptions, disableSearch) {
  return _.defaults(selectorOptions, {
    filter: {
      disableSearch,
      showSelected: true,
      showChanged: false,
    },
    keyMap: selectorKeyMapFunc(i18n),
    styles: selectorStyleOptions,
    allowCheckAll: true,
    showNoDataMessage: true,
  });
}

function generateSchemaForCampaignProperty({
  cacheService,
  dataContextManager,
  i18n,
  dataService = { Campaign: stubDataSource },
  entitySet = {},
  existingCampaignFilter,
}) {
  return {
    name: 'Campaign',
    title: i18n.getString('Entity_Campaign'),
    operatorDisplayNameMap: {
      containsany: i18n.getString('FilterOperator_ContainsAny'),
    },
    operators: ['contains'],
    type: 'id',
    addtionalParams: {
      dataSource: new CampaignDataSource({
        cacheService,
        dataContextManager,
        jsdata: dataService,
        entitySet,
        existingCampaignFilter,
      }),
      selectorOptions: getSelectorOptions(i18n, {
        handleFilterChange: handleFilterChangeForCampaign,
      }),
      maxCount: MAX_SELECTION_COUNT,
    },
    filterable: true,
  };
}

function generateSchemaForAdGroupProperty({
  cacheService,
  dataContextManager,
  i18n,
  dataService = { AdGroup: stubDataSource },
  entitySet = {},
  currentCampaignName = '',
  currentCampaignId = 0,
  isInAccountScope = false,
}) {
  return {
    name: 'AdGroup',
    title: i18n.getString('Entity_Adgroup'),
    operatorDisplayNameMap: {
      containsany: i18n.getString('FilterOperator_ContainsAny'),
    },
    operators: ['contains'],
    type: 'id',
    addtionalParams: {
      dataSource: new AdGroupDataSource({
        cacheService,
        dataContextManager,
        jsdata: dataService,
        entitySet,
        currentCampaignName,
        currentCampaignId,
      }),
      selectorOptions: getSelectorOptions(i18n, {
        handleFilterChange: handleFilterChangeForAdGroup,
      }, isInAccountScope),
      maxCount: MAX_SELECTION_COUNT,
    },
    filterable: true,
  };
}

function generateSchemaForKeywordProperty({
  cacheService,
  dataContextManager,
  i18n,
  dataService = { Keyword: stubDataSource },
  entitySet = {},
}) {
  return {
    name: 'Keyword',
    title: i18n.getString('Entity_Keywords'),
    operatorDisplayNameMap: {
      containsany: i18n.getString('FilterOperator_ContainsAny'),
    },
    operators: ['contains'],
    type: 'id',
    addtionalParams: {
      dataSource: new KeywordDataSource({
        cacheService,
        dataContextManager,
        jsdata: dataService,
        entitySet,
      }),
      selectorOptions: getSelectorOptions(i18n, {
        handleFilterChange: handleFilterChangeForKeyword,
      }, true),
      maxCount: MAX_SELECTION_COUNT,
    },
    filterable: true,
  };
}

function updateEntitySchemaForAccountScope({
  customizedSchemaForProcessor,
  edmCampaign,
  jsdata,
  cacheService,
  dataContextManager,
  i18n,
  existingCampaignFilter,
  customizedGenerateSchemaForAdGroupProperty,
  customizedGenerateSchemaForCampaignProperty,
}) {
  const ops = {
    cacheService,
    dataContextManager,
    i18n,
    isInAccountScope: true,
    getSelectorOptions,
    handleFilterChangeForCampaign,
  };
  let generateCampaignProperty = generateSchemaForCampaignProperty;
  if (customizedGenerateSchemaForCampaignProperty) {
    generateCampaignProperty = customizedGenerateSchemaForCampaignProperty;
  }
  let generateAdGroupProperty = generateSchemaForAdGroupProperty;
  if (customizedGenerateSchemaForAdGroupProperty) {
    generateAdGroupProperty = customizedGenerateSchemaForAdGroupProperty;
  }
  return () => {
    customizedSchemaForProcessor.properties.Campaign =
      generateCampaignProperty(_.extend({}, ops, {
        dataService: jsdata,
        entitySet: edmCampaign.currentAccount.Campaigns,
        existingCampaignFilter,
        maxCount: MAX_SELECTION_COUNT,
        dataSource: new CampaignDataSource({
          cacheService,
          dataContextManager,
          jsdata,
          entitySet: {},
          existingCampaignFilter,
        }),
      }));
    customizedSchemaForProcessor.properties.AdGroup =
      generateAdGroupProperty(_.extend({}, ops, {
        dataSource: new AdGroupDataSource({
          cacheService,
          dataContextManager,
          jsdata: { AdGroup: stubDataSource },
          entitySet: {},
          currentCampaignName: '',
          currentCampaignId: 0,
        }),
      }));
    customizedSchemaForProcessor.properties.Keyword = generateSchemaForKeywordProperty(ops);
    return customizedSchemaForProcessor;
  };
}

function updateEntitySchemaForCampaignScope({
  scope,
  customizedSchemaForProcessor,
  edmCampaign,
  jsdata,
  cacheService,
  dataContextManager,
  i18n,
  existingCampaignFilter,
  customizedGenerateSchemaForAdGroupProperty,
  customizedGenerateSchemaForCampaignProperty,
}) {
  const ops = {
    cacheService,
    dataContextManager,
    i18n,
    getSelectorOptions,
    handleFilterChangeForCampaign,
  };
  let generateCampaignProperty = generateSchemaForCampaignProperty;
  if (customizedGenerateSchemaForCampaignProperty) {
    generateCampaignProperty = customizedGenerateSchemaForCampaignProperty;
  }
  let generateAdGroupProperty = generateSchemaForAdGroupProperty;
  if (customizedGenerateSchemaForAdGroupProperty) {
    generateAdGroupProperty = customizedGenerateSchemaForAdGroupProperty;
  }
  return () => {
    const entitySet = edmCampaign.currentCampaign.AdGroups;
    customizedSchemaForProcessor.properties.AdGroup =
      generateAdGroupProperty(_.extend({}, ops, {
        dataService: jsdata,
        entitySet,
        currentCampaignName: scope.campaignName,
        currentCampaignId: scope.campaignId,
        dataSource: new AdGroupDataSource({
          cacheService,
          dataContextManager,
          jsdata,
          entitySet,
          currentCampaignName: scope.campaignName,
          currentCampaignId: scope.campaignId,
        }),
      }));
    customizedSchemaForProcessor.properties.Campaign =
      generateCampaignProperty(_.extend({}, ops, {
        dataService: jsdata,
        entitySet: edmCampaign.currentAccount.Campaigns,
        existingCampaignFilter,
        maxCount: MAX_SELECTION_COUNT,
        dataSource: new CampaignDataSource({
          cacheService,
          dataContextManager,
          jsdata,
          entitySet: {},
          existingCampaignFilter,
        }),
      }));
    customizedSchemaForProcessor.properties.Keyword = generateSchemaForKeywordProperty(ops);
    return customizedSchemaForProcessor;
  };
}

function updateEntitySchemaForAdGroupScope({
  customizedSchemaForProcessor,
  cacheService,
  dataContextManager,
  i18n,
}) {
  const ops = {
    cacheService,
    dataContextManager,
    i18n,
  };
  return () => {
    customizedSchemaForProcessor.properties.AdGroup = generateSchemaForAdGroupProperty(ops);
    customizedSchemaForProcessor.properties.Campaign = generateSchemaForCampaignProperty(ops);
    customizedSchemaForProcessor.properties.Keyword = generateSchemaForKeywordProperty(ops);
    return customizedSchemaForProcessor;
  };
}

function updateEntitySchemaForCustomerScope({
  customizedSchemaForProcessor,
  i18n,
  generateSchemaForAccountProperty,
}) {
  return () => {
    customizedSchemaForProcessor.properties.Account = generateSchemaForAccountProperty({ i18n });
    return customizedSchemaForProcessor;
  };
}

export function createSchemaProcessorForCurrentScope(options) {
  switch (options.scope.levelAt) {
    case levelAtConstant.CUSTOMER:
      return updateEntitySchemaForCustomerScope(options);
    case levelAtConstant.ACCOUNT:
      return updateEntitySchemaForAccountScope(options);
    case levelAtConstant.CAMPAIGN:
      return updateEntitySchemaForCampaignScope(options);
    case levelAtConstant.ADGROUP:
    default:
      return updateEntitySchemaForAdGroupScope(options);
  }
}
